<template>
  <div class="veritas-letter-doc">
    <div class="no-print control-bar">
      <div class="d-flex align-items-center">
        <h4 class="mb-0">
          {{ schoolName }}
          <span class="text-primary">| Appointment Letter</span>
        </h4>
        <b-badge v-show="!loading" variant="light-primary" class="ml-2">
          {{ reportItems.length }} Letters
        </b-badge>
      </div>
      <div class="action-buttons">
        <button class="btn-modern btn-print" @click="print">
          <feather-icon icon="PrinterIcon" size="18" class="mr-50" />
          <span>Print</span>
        </button>
        <button class="btn-modern btn-close" @click="close">
          <feather-icon icon="XIcon" size="18" class="mr-50" />
          <span>Close</span>
        </button>
      </div>
    </div>

    <div v-if="loading" class="text-center py-5">
      <b-spinner variant="primary" style="width: 3.5rem; height: 3.5rem;" />
      <p class="mt-3 font-weight-bold">Loading appointment letters...</p>
    </div>

    <div v-else-if="error" class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-danger mb-3" />
      <h4 class="text-danger font-weight-bold">{{ error }}</h4>
      <b-button variant="primary" class="mt-3" @click="loadReportData">
        Retry
      </b-button>
    </div>

    <div v-else class="documents-wrap">
      <div
        v-for="(staff, index) in reportItems"
        :key="staff.id || index"
        class="letter-set"
      >
        <article class="appointment-letter">
          <header class="letterhead">
            <img
              :src="letterheadUrl"
              :alt="schoolName"
              class="letterhead__logo"
              @error="onLetterheadError"
            />
            <div class="letterhead__text">
              <p class="letterhead__school">{{ schoolName }}</p>
              <p v-if="campusName" class="letterhead__campus">{{ campusName }}</p>
              <p v-if="displayCampusAddress" class="letterhead__address">
                {{ displayCampusAddress }}
              </p>
            </div>
          </header>

          <div class="letter-body">
            <div class="meta-row">
              <span class="meta-ref">Ref No. {{ referenceNumber(staff) }}</span>
              <span class="meta-date">Date: {{ currentDateLabel }}</span>
            </div>

            <p class="recipient-name">{{ staffDisplayName(staff) }},</p>
            <p v-if="staffAddressLine(staff)" class="recipient-address">
              ({{ staffAddressLine(staff) }})
            </p>

            <div class="subject-banner subject-bar">
              Ref: Appointment Letter for the Position of
              {{ staffDesignation(staff) }}
            </div>

            <p class="salutation">Dear {{ staffDisplayName(staff) }},</p>
            <p class="body-text">
              It is our pleasure to offer you a position at
              {{ schoolName }}. We hope that you will contribute your skills
              towards the betterment of our school and provide the best
              knowledge to our students.
            </p>

            <p class="body-text terms-intro">
              Please read the terms and conditions as follows:
            </p>

            <table class="terms-table">
              <tbody>
                <tr>
                  <td class="terms-table__num">1-</td>
                  <td class="terms-table__label">Designation</td>
                  <td class="terms-table__value">
                    {{ staffDesignation(staff) }}
                  </td>
                </tr>
                <tr>
                  <td class="terms-table__num">2-</td>
                  <td class="terms-table__label">Date of Joining</td>
                  <td class="terms-table__value">
                    {{ joiningDateLabel(staff) }}
                  </td>
                </tr>
                <tr>
                  <td class="terms-table__num">3-</td>
                  <td class="terms-table__label">Gross Salary p.m.</td>
                  <td class="terms-table__value">
                    {{ salaryLine(staff) }}
                  </td>
                </tr>
                <tr>
                  <td class="terms-table__num">4-</td>
                  <td class="terms-table__label">Timings</td>
                  <td class="terms-table__value">
                    {{ timingsLabel(staff) }}
                  </td>
                </tr>
                <tr>
                  <td class="terms-table__num">5-</td>
                  <td class="terms-table__label">
                    Benefits, Deductions &amp; Roster
                  </td>
                  <td class="terms-table__value">
                    as per {{ letterShortName }} Rules &amp; Regulations.
                  </td>
                </tr>
              </tbody>
            </table>

            <p class="body-text">
              The policies for part time faculty will be shared with you and
              will be submitted in original to HR after signing.
            </p>

            <p class="body-text">
              At {{ letterShortName }}, we believe when effective teams are in
              place, the collective product is better than the sum of the
              individual's efforts. We welcome you to our team and wish to have
              a long-term fruitful association.
            </p>

            <p class="body-text">
              Please feel free to contact HR for any further queries/information.
            </p>

            <p class="closing">Best Regards,</p>
          </div>

          <div class="signature-row signatures">
            <div class="signature-block">
              <div class="signature-space"></div>
              <div class="signature-line"></div>
              <p class="signature-title">Principal</p>
            </div>
            <div class="signature-block">
              <div class="signature-space"></div>
              <div class="signature-line"></div>
              <p class="signature-title">Director</p>
            </div>
          </div>
        </article>
      </div>
    </div>
  </div>
</template>

<script>
import { BButton, BSpinner, BBadge } from "bootstrap-vue";
import {
  parseStaffIds,
  parseKentDepIds,
  loadKentCampusInfo,
  loadKentStaffRecords,
  loadKentStaffByDepartments,
  staffDisplayName,
  staffDesignation,
  staffSalary,
  formatKentCurrency,
  staffAddress,
  staffField,
  isGeneralStaffLetterDomain,
  VERITAS_LETTERHEAD_IMAGE,
  usesCampusLogoLetterhead,
  generalStaffLetterRefPrefix,
  generalStaffLetterShortName,
} from "./kentStaffPrintUtils";
import { getMediaPreviewUrl, handleDrivePreviewError } from "@/utils/gdriveUpload";

function ordinalDay(day) {
  const n = Number(day);
  if (n >= 11 && n <= 13) return `${n}th`;
  const mod = n % 10;
  if (mod === 1) return `${n}st`;
  if (mod === 2) return `${n}nd`;
  if (mod === 3) return `${n}rd`;
  return `${n}th`;
}

function formatVeritasLetterDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const day = ordinalDay(date.getDate());
  const month = date
    .toLocaleDateString("en-GB", { month: "short" })
    .toUpperCase();
  const year = date.getFullYear();
  return `${day} ${month} ${year}`;
}

export default {
  name: "VeritasAppointmentLetterPrint",
  components: {
    BButton,
    BSpinner,
    BBadge,
  },
  data() {
    return {
      loading: false,
      error: null,
      reportItems: [],
      schoolName: "Veritas Schooling System",
      campusName: "",
      campusAddress: "",
      letterheadUrl: VERITAS_LETTERHEAD_IMAGE,
      letterheadFallback: "/Myskool-icon-01.svg",
      campusSchoolLogo: "",
      db: "",
      cID: 0,
      staffIDs: [],
      depIDs: [],
      staffStatus: "present",
      reportDomain: (this.$store.state.report || "").replace(/_/g, ""),
    };
  },
  computed: {
    currentDateLabel() {
      return formatVeritasLetterDate(new Date());
    },
    displayCampusAddress() {
      return (this.campusAddress || "").trim();
    },
    letterShortName() {
      return generalStaffLetterShortName(this.reportDomain, this.schoolName);
    },
  },
  created() {
    this.initReport();
  },
  methods: {
    staffDisplayName,
    staffDesignation,
    initReport() {
      if (!isGeneralStaffLetterDomain(this.reportDomain)) {
        this.error = "This report is not available for this school.";
        return;
      }

      const query = this.$route.query;
      this.db = query.db || this.$store.state.userData.db;
      this.cID = Number(query.cID || this.$store.state.userData.cId || 0);
      this.staffIDs = parseStaffIds(query);
      this.depIDs = parseKentDepIds(query);
      this.staffStatus = query.status || "present";

      if (!this.db) {
        this.error = "Database identifier is missing.";
        return;
      }
      if (!this.staffIDs.length && !this.depIDs.length) {
        this.error =
          "No staff or department selected for appointment letters.";
        return;
      }

      this.loadReportData();
    },
    staffAppointedOn(staff) {
      return (
        staff?.appointedOn ||
        staff?.appointmentDate ||
        staff?.joiningDate ||
        null
      );
    },
    staffAddressLine(staff) {
      const addr = staffAddress(staff);
      return addr && addr !== "—" ? addr : "";
    },
    referenceNumber(staff) {
      const fromStaff = staffField(
        staff,
        [
          "refNo",
          "refNumber",
          "referenceNo",
          "appointmentRef",
          "letterRef",
        ],
        ""
      );
      if (fromStaff && fromStaff !== "—") return fromStaff;
      const prefix = generalStaffLetterRefPrefix(this.reportDomain);
      const id = staff?.id;
      return id ? `${prefix}/${id}` : `${prefix}/—`;
    },
    joiningDateLabel(staff) {
      const raw = this.staffAppointedOn(staff);
      if (!raw) return "—";
      return formatVeritasLetterDate(raw);
    },
    salaryLine(staff) {
      const salary = staffSalary(staff);
      if (salary === null) return "PKR. —/- (Inclusive of all allowances)";
      return `PKR. ${formatKentCurrency(salary)}/- (Inclusive of all allowances)`;
    },
    timingsLabel(staff) {
      const fromNote = staffField(staff, ["note", "notes"], "");
      if (fromNote) return fromNote;

      const raw = staff?.timings;
      if (typeof raw === "string" && raw.trim()) return raw.trim();
      if (Array.isArray(raw) && raw.length) {
        return raw
          .map((item) => {
            if (typeof item === "string") return item;
            return item?.day || item?.name || item?.title || "";
          })
          .filter(Boolean)
          .join(", ");
      }
      return staffField(staff, ["timing", "workingDays", "workDays"], "—");
    },
    async loadReportData() {
      this.loading = true;
      this.error = null;

      try {
        const campus = await loadKentCampusInfo(
          this,
          this.db,
          this.cID,
          this.reportDomain
        );
        this.schoolName = campus.schoolName;
        this.campusName = campus.campusName;
        this.campusAddress = campus.campusAddress;
        this.campusSchoolLogo = campus.schoolLogo || "";
        this.setLetterheadUrl(this.campusSchoolLogo);
        if (this.staffIDs.length) {
          this.reportItems = await loadKentStaffRecords(
            this,
            this.db,
            this.cID,
            this.staffIDs
          );
        } else {
          this.reportItems = await loadKentStaffByDepartments(
            this,
            this.db,
            this.cID,
            this.depIDs,
            this.staffStatus
          );
        }

        if (!this.reportItems.length) {
          this.error = "No staff records found for the selected staff.";
        }
      } catch (err) {
        console.error("Veritas appointment letter error:", err);
        this.error = "Failed to load staff records for appointment letters.";
      } finally {
        this.loading = false;
      }
    },
    setLetterheadUrl(schoolLogo) {
      if (usesCampusLogoLetterhead(this.reportDomain)) {
        if (!schoolLogo) {
          this.letterheadUrl = this.letterheadFallback;
          return;
        }
        const raw = String(schoolLogo);
        if (raw.startsWith("blob:") || /^https?:\/\//i.test(raw)) {
          this.letterheadUrl = raw;
          return;
        }
        this.letterheadUrl = getMediaPreviewUrl(schoolLogo, "Image");
        return;
      }
      this.letterheadUrl = VERITAS_LETTERHEAD_IMAGE;
    },
    onLetterheadError(event) {
      if (
        usesCampusLogoLetterhead(this.reportDomain) &&
        this.campusSchoolLogo &&
        !String(this.campusSchoolLogo).startsWith("blob:")
      ) {
        handleDrivePreviewError(event, this.campusSchoolLogo, "image");
        return;
      }
      if (event.target.src !== this.letterheadFallback) {
        event.target.src = this.letterheadFallback;
      }
    },
    print() {
      window.print();
    },
    close() {
      window.close();
    },
  },
};
</script>

<style lang="scss" scoped>
@import url("https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700&family=Source+Serif+4:wght@400;600;700&display=swap");

$text: #111827;
$accent: #0c2340;
$accent-light: #1a4a7a;
$gold: #b8860b;
$font-body: "Source Serif 4", "Georgia", "Times New Roman", serif;
$font-display: "Cormorant Garamond", "Georgia", serif;

.veritas-letter-doc {
  background: #e8ecf2;
  min-height: 100vh;
  padding: 2rem;
  font-family: $font-body;
  color: $text;
}

.control-bar {
  background: #fff;
  padding: 1rem 1.5rem;
  margin-bottom: 1.5rem;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 1rem;
  z-index: 10;
  font-family: "Segoe UI", sans-serif;
}

.action-buttons {
  display: flex;
  gap: 10px;
}

.btn-modern {
  display: inline-flex;
  align-items: center;
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  font-weight: 700;
  cursor: pointer;
  font-family: "Segoe UI", sans-serif;
}

.btn-print {
  background: linear-gradient(135deg, $accent 0%, #2d4a7a 100%);
  color: #fff;
}

.btn-close {
  background: #fff;
  border: 1px solid #dfe6e9;
  color: #636e72;
}

.documents-wrap {
  margin: 0 auto;
}

.letter-set {
  margin-bottom: 2rem;
}

.appointment-letter {
  zoom: 1 !important;
  box-sizing: border-box;
  width: 210mm;
  height: 296mm;
  min-height: 296mm;
  max-height: 296mm;
  padding: 14mm 16mm;
  margin: 20px auto;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  color: $text;
  font-size: 13pt;
  line-height: 1.6;
  page-break-after: avoid;
  page-break-inside: avoid;
  break-after: avoid;
  break-inside: avoid;
  border-radius: 2px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  p {
    margin: 0 0 14px;
  }

  .letter-body {
    flex: 1 1 auto;
    min-height: 0;
  }

  .signatures {
    margin-top: auto;
    flex-shrink: 0;
  }
}

.appointment-letter .letterhead {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 4mm;
  flex-shrink: 0;
  padding-bottom: 0.35rem;
  border-bottom: 1px solid rgba(12, 35, 64, 0.15);

  &__logo {
    flex-shrink: 0;
    width: 22mm;
    max-width: 22mm;
    height: auto;
    max-height: 20mm;
    object-fit: contain;
    object-position: left center;
  }

  &__text {
    flex: 1;
    min-width: 0;
    text-align: center;
  }

  &__school {
    margin: 0;
    font-size: 13pt;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: $accent;
    line-height: 1.25;
  }

  &__campus {
    margin: 0.2rem 0 0;
    font-size: 10.5pt;
    font-weight: 700;
    color: $accent-light;
    line-height: 1.3;
  }

  &__address {
    margin: 0.12rem 0 0;
    font-size: 9.5pt;
    font-weight: 600;
    color: #4b5563;
    line-height: 1.35;
  }
}

.appointment-letter .meta-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 1rem;
  margin-bottom: 0;
  font-weight: 700;
  color: #000;
  flex-shrink: 0;
}

.meta-ref,
.meta-date {
  white-space: nowrap;
}

.appointment-letter .recipient-name {
  font-weight: 700;
  color: #000;
}

.appointment-letter .recipient-address {
  font-weight: 600;
  color: #374151;
}

.appointment-letter .subject-bar,
.appointment-letter .subject-banner {
  padding: 10px 12px;
  margin: 14px 0;
  font-size: 13.5pt;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(90deg, $accent 0%, $accent-light 100%);
  border-left: 4px solid $gold;
  line-height: 1.4;
}

.appointment-letter .salutation {
  font-weight: 700;
  color: #000;
}

.appointment-letter .body-text {
  text-align: justify;
  font-weight: 600;
  color: #111;
  hyphens: auto;
}

.appointment-letter .terms-intro {
  font-weight: 700;
}

.appointment-letter .terms-table {
  width: 100%;
  border-collapse: collapse;
  margin: 10px 0 18px;
  flex-shrink: 0;

  td {
    padding: 12px 10px;
    font-size: 13pt;
    vertical-align: middle;
    border: 1px solid #374151;
    font-weight: 600;
    color: #000;
  }

  &__num {
    width: 2.25rem;
    text-align: center;
    font-weight: 700;
    background: #f3f4f6;
  }

  &__label {
    width: 36%;
    font-weight: 700;
    background: #f9fafb;
  }

  &__value {
    font-weight: 600;
  }
}

.appointment-letter .closing {
  font-weight: 700;
  color: #000;
}

.appointment-letter .signature-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 2.5rem;
  padding-top: 0.5rem;
}

.signature-block {
  flex: 1;
  text-align: center;
  max-width: 45%;
}

.signature-space {
  height: 2.75rem;
  min-height: 2.75rem;
}

.signature-line {
  border-top: 1.5px solid #000;
  margin: 0 0 0.4rem;
}

.signature-title {
  margin: 0;
  font-size: 11pt;
  font-weight: 700;
  color: #000;
  letter-spacing: 0.02em;
}

@media print {
  .no-print {
    display: none !important;
  }

  .veritas-letter-doc {
    background: #fff;
    padding: 0;
  }

  .documents-wrap {
    max-width: none;
    margin: 0;
  }

  .letter-set {
    margin: 0;
  }

  .letter-set + .letter-set {
    page-break-before: always;
    break-before: page;
  }

  .documents-wrap,
  .letter-set {
    width: 210mm !important;
    max-width: 210mm !important;
    min-width: 0 !important;
    padding: 0 !important;
    overflow: visible !important;
  }

  .veritas-letter-doc {
    width: 210mm !important;
    max-width: 210mm !important;
    overflow: hidden !important;
  }

  .appointment-letter {
    box-shadow: none !important;
    margin: 0 !important;
    border-radius: 0;
    zoom: 1 !important;
    width: 210mm !important;
    height: 296mm !important;
    min-height: 296mm !important;
    max-height: 296mm !important;
    padding: 14mm 16mm !important;
    page-break-inside: avoid !important;
    break-inside: avoid !important;
    page-break-after: avoid !important;
    break-after: avoid !important;
  }

  .appointment-letter .terms-table {
    width: 100% !important;
    table-layout: fixed !important;

    td {
      border: 1pt solid #000 !important;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }

    .terms-table__num,
    .terms-table__label {
      background: #f3f4f6 !important;
      font-weight: 700;
    }
  }

  .appointment-letter .subject-bar,
  .appointment-letter .subject-banner {
    background: $accent !important;
    border-left: 3pt solid $gold !important;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .appointment-letter .letterhead__logo,
  .appointment-letter .terms-table td {
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  .appointment-letter .signature-line {
    border-top: 1.5pt solid #000 !important;
  }
}
</style>

<style lang="scss">
@import "./veritasLetterPrint.global.scss";
</style>
